// ─── Pakistan-time helpers (single source of truth) ───────────────────────
//
// The backend stores instant columns (attendance clock_in_at / clock_out_at,
// created_at, …) as UTC. The API serialises them as ISO strings with a
// trailing `Z` — e.g. "2026-07-22T02:22:57.494Z" (that is 07:22 AM in Karachi).
// Pakistan is a fixed UTC+5 with no daylight saving.
//
// Use these to render a stored UTC instant in Pakistan local time, and to move
// a <input type="datetime-local"> value (which the user reads/edits as Pakistan
// time) back to the UTC wall-clock string the tz-naive columns expect.
//
// ⚠ Do NOT use these on DATE-only values (attendance_date, scheduled_date, …).
// Those carry no time-of-day; shifting them by a timezone would move the day.

const PK_TZ = 'Asia/Karachi';

// Turn an API value into a real Date instant. Handles ISO strings with `Z` or
// an explicit offset (parsed as-is), and bare "YYYY-MM-DD HH:mm:ss" values
// (which the columns represent as UTC, so we tag them Z before parsing).
const toInstant = (ts) => {
  if (!ts) return null;
  if (ts instanceof Date) return isNaN(ts.getTime()) ? null : ts;
  let s = String(ts).trim();
  if (!s) return null;
  const hasZone = /[zZ]$/.test(s) || /[+-]\d\d:?\d\d$/.test(s);
  if (!hasZone) s = `${s.replace(' ', 'T')}Z`;
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
};

const timeFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: PK_TZ, hour: 'numeric', minute: '2-digit', hour12: true,
});
const dateFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: PK_TZ, year: 'numeric', month: 'short', day: 'numeric',
});
const dateTimeFmt = new Intl.DateTimeFormat('en-US', {
  timeZone: PK_TZ, year: 'numeric', month: 'short', day: 'numeric',
  hour: 'numeric', minute: '2-digit', hour12: true,
});

// "2:22 PM" in Pakistan time.
export const fmtPKTTime = (ts, fallback = '—') => {
  const d = toInstant(ts);
  return d ? timeFmt.format(d) : fallback;
};

// "Jul 22, 2026" in Pakistan time. (For instant columns only — not DATE columns.)
export const fmtPKTDate = (ts, fallback = '—') => {
  const d = toInstant(ts);
  return d ? dateFmt.format(d) : fallback;
};

// "Jul 22, 2026, 2:22 PM" in Pakistan time.
export const fmtPKTDateTime = (ts, fallback = '—') => {
  const d = toInstant(ts);
  return d ? dateTimeFmt.format(d) : fallback;
};

// Pakistan-local Y/M/D/H/m parts of a UTC instant, zero-padded.
const pktParts = (d) =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: PK_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d).reduce((o, p) => { o[p.type] = p.value; return o; }, {});

// Stored UTC instant -> "YYYY-MM-DDTHH:mm" in Pakistan time, for the value of
// an <input type="datetime-local">. Empty string when absent/invalid.
export const utcToPKTInput = (ts) => {
  const d = toInstant(ts);
  if (!d) return '';
  const p = pktParts(d);
  const hh = p.hour === '24' ? '00' : p.hour; // en-CA can emit '24' at midnight
  return `${p.year}-${p.month}-${p.day}T${hh}:${p.minute}`;
};

// Pakistan-local datetime-local value ("YYYY-MM-DDTHH:mm") -> UTC wall-clock
// string "YYYY-MM-DDTHH:mm:ss" (no zone) for the tz-naive UTC columns.
// Pakistan is a fixed UTC+5, so we anchor the input at +05:00 and read the UTC
// fields back out. Empty in -> empty out.
export const pktInputToUTC = (v) => {
  if (!v) return '';
  const withSecs = v.length === 16 ? `${v}:00` : v;
  const d = new Date(`${withSecs}+05:00`);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 19); // UTC "YYYY-MM-DDTHH:mm:ss"
};

// "YYYY-MM-DD" for the current calendar date in Pakistan (for date-range
// filter defaults, so "today" never lands on the wrong side of midnight).
export const todayPKT = () =>
  new Intl.DateTimeFormat('en-CA', {
    timeZone: PK_TZ, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(new Date());
